import { useEffect, useState } from 'react';
import { Stack, Cluster, Text, Input, Button, Badge, Banner, Skeleton, Tabs, type TabItem } from '../../ui';
import {
  fetchSuppressions, addSuppression, removeSuppression,
  type SuppressionRow, type NotifyChannel,
} from '../../../lib/notifications-admin';

const KIND_TABS: TabItem<NotifyChannel>[] = [
  { value: 'sms', label: 'SMS (phone)' },
  { value: 'email', label: 'Email' },
];

// Rows we never remove casually: opting someone back in after they STOPped or
// complained is a compliance decision, so we make the admin confirm.
const PROTECTED = new Set(['stop', 'complaint']);

function reasonTone(reason: string): 'danger' | 'warning' | 'neutral' {
  if (reason === 'stop' || reason === 'complaint') return 'danger';
  if (reason === 'bounce' || reason === 'undeliverable') return 'warning';
  return 'neutral';
}

export function SuppressionsTab() {
  const [kind, setKind] = useState<NotifyChannel>('sms');
  const [rows, setRows] = useState<SuppressionRow[] | null>(null);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  function load(k: NotifyChannel) {
    setRows(null);
    fetchSuppressions(k)
      .then(setRows)
      .catch((e) => setError(e?.message ?? 'Failed to load.'));
  }
  useEffect(() => { load(kind); /* eslint-disable-next-line */ }, [kind]);

  const valid = kind === 'sms'
    ? /^\+\d{8,15}$/.test(draft.trim())
    : /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.trim());

  async function add() {
    const value = draft.trim();
    if (!valid || busy) return;
    setBusy(true); setError(null);
    try {
      await addSuppression(kind, kind === 'email' ? value.toLowerCase() : value);
      setDraft('');
      load(kind);
    } catch (e: any) {
      setError(e?.message ?? 'Could not add.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(value: string) {
    setError(null);
    setConfirming(null);
    setRows((prev) => prev?.filter((r) => r.value !== value) ?? prev); // optimistic
    try {
      await removeSuppression(kind, value);
    } catch (e: any) {
      setError(e?.message ?? 'Could not remove.');
      load(kind);
    }
  }

  const filtered = (rows ?? []).filter((r) => r.value.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <Stack gap="group">
      <Tabs variant="segmented" items={KIND_TABS} value={kind} onChange={setKind} aria-label="Suppression list" />

      <Text role="caption" muted as="p">
        {kind === 'sms'
          ? 'Numbers we never text. STOP replies and undeliverables land here automatically; you can also add one manually.'
          : 'Emails we never send to. Hard bounces and spam complaints land here automatically.'}
      </Text>

      {error && <Banner tone="danger" title="Problem" onClose={() => setError(null)}>{error}</Banner>}

      <Cluster gap="tight" align="center">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder={kind === 'sms' ? 'Add a number, E.164 e.g. +15551234567' : 'Add an email address'}
          aria-label={kind === 'sms' ? 'Phone to suppress' : 'Email to suppress'}
          className="max-w-[340px]"
        />
        <Button variant="secondary" onClick={add} disabled={!valid} loading={busy}>Add</Button>
      </Cluster>

      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search this list"
        aria-label="Search suppressions"
        className="max-w-[340px]"
      />

      {!rows ? (
        <Stack gap="tight">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</Stack>
      ) : filtered.length === 0 ? (
        <Banner tone="info" title={query ? 'No matches' : 'Nothing suppressed yet'}>
          {query ? 'No entries match your search.' : 'STOP replies and bounces will appear here automatically.'}
        </Banner>
      ) : (
        <div className="overflow-hidden rounded-sm border border-border">
          {filtered.map((r, i) => (
            <div key={r.value} className={`flex items-center gap-3 px-4 py-2.5 ${i ? 'border-t border-border' : ''}`}>
              <span className="flex-1 truncate font-body text-small text-text">{r.value}</span>
              <Badge tone={reasonTone(r.reason)}>{r.reason}</Badge>
              {confirming === r.value ? (
                <Cluster gap="tight">
                  <Text role="caption" muted as="span">Re-allow?</Text>
                  <Button size="sm" variant="primary" tone="danger" onClick={() => remove(r.value)}>Yes, remove</Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>Cancel</Button>
                </Cluster>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  tone="danger"
                  onClick={() => (PROTECTED.has(r.reason) ? setConfirming(r.value) : remove(r.value))}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Stack>
  );
}
